import { capitalize } from 'lodash-es';
import {
  makeSelectAuthApiConfig,
  makeSelectResourceLoadingState,
} from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { selectAuth } from 'services/session/session.selectors';
import { AppState, ReduxSelector } from 'store/types';
import { ValuesType } from 'utility-types';
import {
  closeAccountEndpoint,
  getPinProfileEndpoint,
  getUserBalanceEndpoint,
  getUserProfileEndpoint,
  infoLoginEndpoint,
  modifyProfileContactsEndpoint,
  modifyProfileDomicileEndpoint,
  modifyProfileEmailEndpoint,
  modifyProfilePasswordEndpoint,
  modifyProfilePhoneNumberEndpoint,
  modifyProfileSecurityQuestionEndpoint,
  modifyProfileUsernameEndpoint,
  validateBookingOperationsEndpoint,
  validateDocumentExpirationEndpoint,
  validateIbanEndpoint,
} from './user.config';
import { computeIdDocumentState } from './user.helpers';
import {
  CertifiedContacts,
  CloseAccountPayload,
  GetExpirationDays,
  ModifyContactsPayload,
  ModifyEmailPayload,
  ModifyPhoneNumberPayload,
  ModifySecurityQuestionPayload,
  SGADUserDomicile,
  UserBalance,
  UserProfile,
} from './user.types';

const selectUserState: ReduxSelector<ValuesType<Pick<AppState, 'user'>>> = (
  reduxState: AppState,
) => reduxState.user;

export const selectUserProfile: ReduxSelector<UserProfile> = (
  reduxState: AppState,
) => selectUserState(reduxState).profile;

/**
 * Extracts the pinCode received from
 * GET '/profilo-ms/ms/profile/getAccountPin'.
 * @param reduxState
 */
export const selectUserPinCode: ReduxSelector<string> = (
  reduxState: AppState,
) => selectUserState(reduxState).pinCode;

export const selectLastAccessDate: ReduxSelector<string> = (
  reduxState: AppState,
) => selectUserState(reduxState).lastAccessDate;

export const selectPasswordExpirationDays: ReduxSelector<number> = (
  reduxState: AppState,
) => selectUserState(reduxState).profile.passwordExpirationDays;

/**
 * Selects user fullName.
 * @param reduxState
 */
export const selectUserFullName: ReduxSelector<string> = (
  reduxState: AppState,
) => {
  const { name, surname } = selectUserProfile(reduxState);

  return `${capitalize(name)}\u0020${capitalize(surname)}`;
};

/**
 * Extracts the account balance received from
 * GET '/account-ms/v2/profile/balance'.
 * @param reduxState
 */
export const selectUserBalance: ReduxSelector<UserBalance> = (
  reduxState: AppState,
) => selectUserState(reduxState).balance;

export const selectBillCode: ReduxSelector<string> = (reduxState: AppState) => {
  return selectUserProfile(reduxState).billCode;
};

export const selectUserProfileLoadState = makeSelectResourceLoadingState(
  getUserProfileEndpoint,
);

export const selectUserBalanceLoadState = makeSelectResourceLoadingState(
  getUserBalanceEndpoint,
);

/**
 * Extracts the object we pass to 'requestDataIfNecessary' to request the user
 * data.
 * @param reduxState
 * "see AppState
 */
export const selectGetUserProfileApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: getUserProfileEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: getUserProfileEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
  };
};

/**
 * Extracts the object we pass to 'requestDataIfNecessary' to request
 * the user balance.
 * @param reduxState
 * @see GET '/account-ms/v2/profile/balance'.
 */
export const selectCurrentUserBalanceApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: getUserBalanceEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: getUserBalanceEndpoint,
      method: 'GET',
      enrichRequestData: false,
    })(reduxState),
    invalidateIfPresent: true,
  };
};

/**
 * Extracts the pinCode retrieved from
 * GET '/profilo-ms/ms/profile/getAccountPin'.
 * @param reduxState
 */
export const selectPinCodeApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: getPinProfileEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: getPinProfileEndpoint,
      method: 'GET',
    })(reduxState),
  };
};

/**
 * Returns the api config to modify username.
 * PUT '/profilo-ms/ms/profile/modify/username'.
 * @param reduxState
 */
export function makeSelectModifyProfileUsernameApiConfig(
  username: string,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyProfileUsernameApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      method: 'PUT',
      url: modifyProfileUsernameEndpoint,
      data: {
        username,
      },
    })(reduxState);

    return {
      resourceId: modifyProfileUsernameEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Returns the api config to modify user password.
 * PUT '/profilo-ms/ms/profile/modify/password'.
 * @param reduxState
 */
export function makeSelectModifyUserPasswordApiConfig(
  newPassword: string,
): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyUserPasswordApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      method: 'PUT',
      url: modifyProfilePasswordEndpoint,
      data: {
        newPassword,
      },
    })(reduxState);

    return {
      resourceId: modifyProfilePasswordEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Returns the api config to modify user domicile.
 * PUT '/profilo-ms/ms/profile/modify/domicile'.
 * @param reduxState
 */
export function makeSelectModifyUserDomicileApiConfig({
  postalCode,
  address,
  municipality,
  country,
}: Omit<SGADUserDomicile, 'houseNumber' | 'district'> & {
  municipality: string;
}): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyUserDomicile(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      method: 'PUT',
      url: modifyProfileDomicileEndpoint,
      data: {
        postalCode,
        municipality,
        address,
        country,
      },
    })(reduxState);

    return {
      resourceId: modifyProfileDomicileEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Returns the api config to modify the security question.
 * PUT '/profilo-ms/ms/profile/modify/domicile'
 */
export function makeSelectModifyUserSecurityQuestionApiConfig({
  question,
  questionId,
  answer,
}: ModifySecurityQuestionPayload): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyUserSecurityQuestionApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: modifyProfileSecurityQuestionEndpoint,
      method: 'PUT',
      data: {
        active: true,
        questionId,
        securityAnswer: answer,
        securityQuestion: question,
      },
    })(reduxState);

    return {
      resourceId: modifyProfileSecurityQuestionEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

export const selectInfoLoginEndpointApiConfig: ReduxSelector<
  RequestApiDataIfNecessaryPayload
> = (reduxState: AppState) => {
  return {
    resourceId: infoLoginEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: infoLoginEndpoint,
      method: 'GET',
    })(reduxState),
  };
};

/**
 * Returns the api config to modify the profile contacts.
 * PUT '/profilo-ms/ms/profile/modify/contacts'
 */
export function makeSelectModifyProfileContactsApiConfig({
  phoneNumber,
  phonePrefix,
  email,
}: ModifyContactsPayload): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyProfileContactsApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: modifyProfileContactsEndpoint,
      method: 'PUT',
      data: { phoneNumber, phonePrefix, email },
    })(reduxState);

    return {
      resourceId: modifyProfileContactsEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Returns the api config to modify the profile phone number.
 * PUT '/profilo-ms/ms/profile/update/cellphone'
 */
export function makeSelectModifyProfilePhoneNumberApiConfig({
  phoneNumber,
  phonePrefix,
}: ModifyPhoneNumberPayload): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyProfilePhoneNumberApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: modifyProfilePhoneNumberEndpoint,
      method: 'PUT',
      data: { phoneNumber, phonePrefix },
    })(reduxState);

    return {
      resourceId: modifyProfilePhoneNumberEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Returns the api config to modify the profile email.
 * PUT '/profilo-ms/ms/profile/update/email'
 */
export function makeSelectModifyProfileEmailApiConfig({
  newEmail,
}: ModifyEmailPayload): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function selectModifyProfileEmailApiConfig(reduxState) {
    const requestConfig = makeSelectAuthApiConfig({
      url: modifyProfileEmailEndpoint,
      method: 'PUT',
      data: { newEmail },
    })(reduxState);

    return {
      resourceId: modifyProfileEmailEndpoint,
      requestConfig,
      invalidateIfPresent: true,
    };
  };
}

/**
 * Outputs the current user document classification.
 * @param appState
 */
export function selectUserIdState(appState: AppState) {
  const verifiedAt = selectAuth(appState)?.verifiedAt ?? '';
  const userProfile = selectUserProfile(appState);

  return computeIdDocumentState(userProfile, verifiedAt);
}

/**
 * Extracts the 'documentExpiration' fields recieved from
 * GET '/profilo-ms/ms/profile/getDocumentExpirationDays'.
 * @param reduxState
 */
export const selectGetDocumentExpirationDays: ReduxSelector<
  GetExpirationDays
> = (reduxState: AppState) => selectUserState(reduxState).documentExpiration;

/**
 * Extracts the 'certifiedContacts' fields recieved from
 * GET '/profilo-ms/ms/profile/getContactCertification'.
 * @param reduxState
 */
export const selectGetCertifiedContacts: ReduxSelector<CertifiedContacts> = (
  reduxState: AppState,
) => selectUserState(reduxState).certifiedContacts;

export const makeSelectValidateIbanApiConfig =
  (iban: string) =>
  (reduxState: AppState): RequestApiDataIfNecessaryPayload => ({
    resourceId: validateIbanEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: validateIbanEndpoint,
      method: 'POST',
      data: { iban },
      enrichRequestData: false,
    })(reduxState),
  });

export const makeSelectCloseAccountApiConfig =
  (requestData: CloseAccountPayload) =>
  (reduxState: AppState): RequestApiDataIfNecessaryPayload => ({
    resourceId: closeAccountEndpoint,
    requestConfig: makeSelectAuthApiConfig({
      url: closeAccountEndpoint,
      method: 'POST',
      data: requestData,
      enrichRequestData: false,
    })(reduxState),
  });

export const selectValidateDocumentApiConfig = (
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload => ({
  resourceId: validateDocumentExpirationEndpoint,
  requestConfig: makeSelectAuthApiConfig({
    url: validateDocumentExpirationEndpoint,
    method: 'GET',
    enrichRequestData: false,
  })(reduxState),
});

export const selectBookingOperationsApiConfig = (
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload => ({
  resourceId: validateBookingOperationsEndpoint,
  requestConfig: makeSelectAuthApiConfig({
    url: validateBookingOperationsEndpoint,
    method: 'GET',
    enrichRequestData: false,
  })(reduxState),
  skipDefaultServerErrorHandling: true,
});
